import { useState } from "react";
import PageMeta from "../components/common/PageMeta";
import PageContainer from "../components/common/PageContainer";
import PageHeader from "../components/common/PageHeader";
import { useApi } from "../hooks/useApi";
import { Button, Box, Snackbar, Alert, TextField } from '@mui/material';
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export default function AdminPanel() {
  const { post } = useApi();
  const [daysToSync, setDaysToSync] = useState<number>(7);
  const [cdrnDaysToSync, setCdrnDaysToSync] = useState<number>(7);
  const [shopifyAccountId, setShopifyAccountId] = useState<string>('');
  const [stripeAccountId, setStripeAccountId] = useState<string>('');
  const [meterBillingProfileId, setMeterBillingProfileId] = useState<string>('');
  const [meterName, setMeterName] = useState<string>('');
  const [meterQuantity, setMeterQuantity] = useState<number>(1);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleFrontendSentryError = () => {
    Sentry.captureMessage("Test Frontend Sentry Error - This is a simulated error for testing purposes");
    setSnackbar({
      open: true,
      message: 'Frontend Sentry error has been triggered',
      severity: 'success',
    });
  };

  const handleBackendSentryErrorLocal = async () => {
    try {
      await post(`/api/admin/sentry-error/local`, { level: 'error' });
      setSnackbar({
        open: true,
        message: 'Backend Sentry error (local) has been triggered',
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to trigger backend Sentry error',
        severity: 'error',
      });
    }
  };

  const handleBackendSentryErrorGlobal = async () => {
    try {
      await post(`/api/admin/sentry-error/global`, {});
      setSnackbar({
        open: true,
        message: 'Expected error but no error was returned',
        severity: 'error',
      });

    } catch (err: any) {
      if (err.message === 'Test sentry error' || err.message.includes("Internal Server Error")) {
        setSnackbar({
          open: true,
          message: 'Backend Sentry error (global) has been triggered',
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Something went wrong.',
          severity: 'error',
        });  
      }
    }
  };

  const handleSyncAlerts = async () => {
    try {
      const startDate = dayjs.utc().subtract(daysToSync, 'days').format('YYYY-MM-DD');
      const response = await post<{ inserted: number; updated: number, noop: number }>(`/api/admin/alerts/ethoca/sync`, {
        startDate,
      }, { slowApi: true });
      setSnackbar({
        open: true,
        message: `Ethoca alerts synced successfully. Inserted: ${response.inserted}, Updated: ${response.updated}, Unchanged: ${response.noop}`,
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to sync alerts',
        severity: 'error',
      });
    }
  };

  const handleSyncCdrnAlerts = async () => {
    try {
      const startDate = dayjs.utc().subtract(cdrnDaysToSync, 'days').format('YYYY-MM-DD');
      const endDate = dayjs.utc().format('YYYY-MM-DD');
      const response = await post<{ total: number; updated: number; unchanged: number }>(`/api/admin/alerts/cdrn/sync`, {
        startDate,
        endDate,
      }, { slowApi: true });
      setSnackbar({
        open: true,
        message: `CDRN alerts synced successfully. Total: ${response.total}, Updated: ${response.updated}, Unchanged: ${response.unchanged}`,
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to sync CDRN alerts',
        severity: 'error',
      });
    }
  };

  const handlePopulateStripePaymentsCache = async () => {
    if (!stripeAccountId.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a Stripe Account ID',
        severity: 'error',
      });
      return;
    }
    try {
      const response = await post<{ message: string }>(`/api/admin/stripe/${stripeAccountId}/populate-payments-cache`, {});
      setSnackbar({
        open: true,
        message: response.message,
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to populate Stripe payments cache',
        severity: 'error',
      });
    }
  };

  const handleRecordMeterEvent = async () => {
    if (!meterBillingProfileId.trim() || !meterName.trim()) {
      setSnackbar({
        open: true,
        message: 'Billing profile ID and meter name are required',
        severity: 'error',
      });
      return;
    }
    try {
      await post(`/api/admin/billing/meter-events`, {
        billingProfileId: Number(meterBillingProfileId),
        meterName: meterName.trim(),
        quantity: meterQuantity,
      });
      setSnackbar({
        open: true,
        message: `Meter event "${meterName.trim()}" recorded for billing profile ${meterBillingProfileId} (quantity ${meterQuantity})`,
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to record meter event',
        severity: 'error',
      });
    }
  };

  const handlePopulatePaymentsCache = async () => {
    if (!shopifyAccountId.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a Shopify Account ID',
        severity: 'error',
      });
      return;
    }
    try {
      const response = await post<{ message: string }>(`/api/admin/shopify/${shopifyAccountId}/populate-payments-cache`, {});
      setSnackbar({
        open: true,
        message: response.message,
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to populate payments cache',
        severity: 'error',
      });
    }
  };

  return (
    <div>
      <PageMeta
        title="Chargemont | Admin Panel"
        description="Chargemont | Admin Panel"
      />
      <PageContainer>
        <PageHeader title="Admin Panel" />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600 }}>
          <Button
            variant="contained"
            onClick={handleFrontendSentryError}
            sx={{
              backgroundColor: '#465fff',
              '&:hover': {
                backgroundColor: '#3641f5',
              },
              py: 1.5,
            }}
          >
            Test Frontend Sentry Error
          </Button>

          <Button
            variant="contained"
            onClick={handleBackendSentryErrorLocal}
            sx={{
              backgroundColor: '#465fff',
              '&:hover': {
                backgroundColor: '#3641f5',
              },
              py: 1.5,
            }}
          >
            Test Backend Sentry Error (local)
          </Button>

          <Button
            variant="contained"
            onClick={handleBackendSentryErrorGlobal}
            sx={{
              backgroundColor: '#465fff',
              '&:hover': {
                backgroundColor: '#3641f5',
              },
              py: 1.5,
            }}
          >
            Test Backend Sentry Error (global)
          </Button>

          <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
            <TextField
              label="Days to Sync"
              value={daysToSync}
              onChange={(e) => setDaysToSync(Number(e.target.value))}
              sx={{ width: '30%' }}
            />
            <Button
              variant="contained"
              onClick={handleSyncAlerts}
              sx={{
                backgroundColor: '#465fff',
                '&:hover': {
                  backgroundColor: '#3641f5',
                },
                py: 1.5,
                width: '70%',
              }}
            >
              Sync Ethoca Alerts
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Days to Sync"
              value={cdrnDaysToSync}
              onChange={(e) => setCdrnDaysToSync(Number(e.target.value))}
              sx={{ width: '30%' }}
            />
            <Button
              variant="contained"
              onClick={handleSyncCdrnAlerts}
              sx={{
                backgroundColor: '#465fff',
                '&:hover': {
                  backgroundColor: '#3641f5',
                },
                py: 1.5,
                width: '70%',
              }}
            >
              Sync Pending CDRN Alerts
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Shopify Account ID"
              value={shopifyAccountId}
              onChange={(e) => setShopifyAccountId(e.target.value)}
              sx={{ width: '30%' }}
            />
            <Button
              variant="contained"
              onClick={handlePopulatePaymentsCache}
              sx={{
                backgroundColor: '#465fff',
                '&:hover': {
                  backgroundColor: '#3641f5',
                },
                py: 1.5,
                width: '70%',
              }}
            >
              Populate Shopify Payments Cache
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Stripe Account ID"
              value={stripeAccountId}
              onChange={(e) => setStripeAccountId(e.target.value)}
              sx={{ width: '30%' }}
            />
            <Button
              variant="contained"
              onClick={handlePopulateStripePaymentsCache}
              sx={{
                backgroundColor: '#465fff',
                '&:hover': {
                  backgroundColor: '#3641f5',
                },
                py: 1.5,
                width: '70%',
              }}
            >
              Populate Stripe Payments Cache
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Billing Profile ID"
              value={meterBillingProfileId}
              onChange={(e) => setMeterBillingProfileId(e.target.value)}
              sx={{ width: '20%' }}
            />
            <TextField
              label="Meter Name"
              value={meterName}
              onChange={(e) => setMeterName(e.target.value)}
              sx={{ width: '30%' }}
            />
            <TextField
              label="Quantity"
              type="number"
              value={meterQuantity}
              onChange={(e) => setMeterQuantity(Number(e.target.value))}
              sx={{ width: '15%' }}
            />
            <Button
              variant="contained"
              onClick={handleRecordMeterEvent}
              sx={{
                backgroundColor: '#465fff',
                '&:hover': {
                  backgroundColor: '#3641f5',
                },
                py: 1.5,
                flex: 1,
              }}
            >
              Record Billing Meter Event
            </Button>
          </Box>
        </Box>

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackbar.open}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </PageContainer>
    </div>
  );
}

